/*
章の索引。学習パスの画面と、クラウドエディターの最初の画面 (どの章を開くか、どの種類の環境を起こすか) はこれだけを読む。
写した章の中身 (読む文、手順、採点の材料) は束に入れず、JSON (apps/client/cloudeditor-guides/<章>.<hash>.json) にして、
章を開いたときに会員だけが 1 本だけ読む (guide-loader.ts)。写した章は 348 本まで増やす予定で、中身ごと束に入れると
学習パスの画面とクラウドエディターの画面に 2MB を超えて乗り、Worker の束では全部の isolate のヒープに起動時から乗るため。

索引の 1 章は短い 1 行に保つ。中身がここへ戻ってくると guide-bundle.test.ts が落ちる。
*/
import type { ItemCompletion, PathItemType } from "./item-completion";
import type { KindName } from "./kinds";
import { LESSON_GUIDE_INDEX } from "./lesson-guides.generated";

export interface GuideEntry {
  id: string;
  /** 環境の種類 (kinds.ts) */
  kind: KindName;
  /** ホーム (~) の下の、開くフォルダ */
  folder: string;
  /** 元のレッスンを写した章なら、そのレッスンの id (lessons.id) */
  lessonId?: string;
  /** 学習パスの項目の本文を写した章なら、その項目の id (team_curriculums.id)。lessonId とは同時に持たない */
  itemId?: string;
  /** 写した章の中身のファイルの名前に入れた、中身のハッシュ。手で書いた章には無い (中身は guides.ts) */
  hash?: string;
  /**
   * 写した章の確かめの版 (convert.py の guide_version。手順の id と確かめの定義だけから作り、本文を直しても変わらない)。
   * 受講生の確かめ (yumesaku check) が報告し、api-go はこの版で通った印だけを合格に数える。手で書いた章には無い
   */
  version?: string;
  /**
   * 本文だけの章 (text 型のレッスンを写したもの)。環境 (Pod) を起こさず、板の「読み終えた」で元のレッスンを完了にする。
   * 中身を読む前に決められるよう、索引にも持つ (Guide.readOnly と同じ値)
   */
  readOnly?: true;
  /**
   * 環境 (Pod) を、この手順に進んでから起こす (手順の id)。学習パスの項目の本文に試せるコードがある章の「端末で試す」。
   * それより前の手順 (本文を読む) の間は、読むだけの章と同じく環境を起こさず、待たせない。中身を読む前に決められるよう、索引に持つ
   */
  envFrom?: string;
  /** 項目の章の、写したときの項目の種類 (team_curriculums.item_type)。項目の章だけが持つ */
  itemType?: PathItemType;
  /** 項目の章が済む道。書いていなければ item-completion.ts の itemGuideCompletion が決める */
  completion?: ItemCompletion;
  /**
   * 練習道場の一覧 (/luagate/dojo) に 1 枚のカードとして並べる、1 章 10 問の道場。道場の名前 (Guide.label) と副題 (Guide.project から名前を除いたもの)。
   * 中身 (guides.ts) を束に入れずに一覧が題を出せるよう索引に持ち、guides.ts と食い違うと cloudeditor.test.ts が落ちる。
   * 章の ID が dojo-<名前> の形の章は必ず持つ (持たないと一覧に出ない道場になるので、同じ test が落ちる)
   */
  dojo?: { name: string; subtitle: string };
  /** 自己申告と読むだけの手順しか無い章。環境 (Pod) を起こさない (Guide.attestOnly と同じ値)。手で書いた章だけが持つ */
  attestOnly?: true;
}

/** 写した章の索引の 1 行 (lesson-guides.generated.ts)。元のレッスンか、学習パスの項目のどちらか一方を持つ */
export type LessonGuideEntry = Omit<GuideEntry, "id" | "lessonId" | "itemId" | "itemType" | "hash" | "version"> & { hash: string; version: string } & (
  | { lessonId: string; itemId?: never; itemType?: never }
  // 項目の章は、読むだけ (readOnly) か、読んでから端末で試す (envFrom) かのどちらか
  | { itemId: string; itemType: PathItemType; lessonId?: never; readOnly: true; envFrom?: never }
  | { itemId: string; itemType: PathItemType; lessonId?: never; readOnly?: never; envFrom: string }
);

/**
 * カメ師範の十の巻の道場の章の索引。練習道場の一覧に出す印 (dojo) は、ここで必ず付く。
 * 名前は guides.ts の Guide.label と同じにする (食い違うと cloudeditor.test.ts が落ちる)
 */
function dojoGuide(kind: GuideEntry["kind"], id: string, name: string): Omit<GuideEntry, "id"> {
  return { kind, folder: id, dojo: { name, subtitle: "カメ師範の十の巻" } };
}

/** 手で書いた章 (guides.ts の WRITTEN_GUIDES) の種類とフォルダ。guides.ts と食い違うと cloudeditor.test.ts が落ちる */
const WRITTEN_GUIDE_INDEX: Readonly<Record<string, Omit<GuideEntry, "id">>> = {
  "first-python": { kind: "python", folder: "first-python" },
  "first-go": { kind: "go", folder: "first-go" },
  "first-web": { kind: "web", folder: "first-web" },
  "todo-app": { kind: "node", folder: "todo-app" },
  "go-algo": { kind: "go", folder: "go-algo" },
  "py-algo": { kind: "python", folder: "py-algo" },
  // Go 道場 fleamaをGoで作り直す10問 (dojo-go-guides.ts)
  "dojo-go-01": { kind: "go", folder: "dojo-go-01" },
  "dojo-go-02": { kind: "go", folder: "dojo-go-02" },
  "dojo-go-03": { kind: "go", folder: "dojo-go-03" },
  "dojo-go-04": { kind: "go", folder: "dojo-go-04" },
  "dojo-go-05": { kind: "go", folder: "dojo-go-05" },
  "dojo-go-06": { kind: "go", folder: "dojo-go-06" },
  "dojo-go-07": { kind: "go", folder: "dojo-go-07" },
  "dojo-go-08": { kind: "go", folder: "dojo-go-08" },
  "dojo-go-09": { kind: "go", folder: "dojo-go-09" },
  "dojo-go-10": { kind: "go", folder: "dojo-go-10" },
  "hw-markdown": { kind: "linux", folder: "hw-markdown" },
  "hw-shortcuts": { kind: "linux", folder: "hw-shortcuts" },
  "hw-search": { kind: "linux", folder: "hw-search" },
  "hw-note": { kind: "linux", folder: "hw-note" },
  "mattermost-join": { kind: "linux", folder: "mattermost-join", attestOnly: true },
  // 問いの手順だけの章。環境を起こさないので readOnly
  "quiz-3a03297d": { kind: "linux", folder: "quiz-3a03297d", readOnly: true },
  "dojo-ai": dojoGuide("python", "dojo-ai", "AI 道場"),
  "dojo-web": dojoGuide("python", "dojo-web", "Web・ネットワーク道場"),
  "dojo-req": dojoGuide("python", "dojo-req", "要件定義道場"),
  "dojo-consul": dojoGuide("python", "dojo-consul", "ITコンサル道場"),
  "hw-oop-report": { kind: "java", folder: "hw-oop-report" },
  "hw-todo-read": { kind: "node", folder: "hw-todo-read" },
  "hw-auth-choice": { kind: "node", folder: "hw-auth-choice" },
  "hw-review-bug": { kind: "java", folder: "hw-review-bug" },
  "hw-class-diagram": { kind: "python", folder: "hw-class-diagram" },
  "hw-sequence-diagram": { kind: "python", folder: "hw-sequence-diagram" },
  "hw-folder-tidy": { kind: "linux", folder: "hw-folder-tidy", attestOnly: true },
  "hw-pc-spec": { kind: "linux", folder: "hw-pc-spec", attestOnly: true },
  "hw-permissions": { kind: "linux", folder: "hw-permissions" },
  "db-why-database": { kind: "db", folder: "db-why-database" },
  "db-setup-postgres": { kind: "db", folder: "db-setup-postgres" },
  "hw-web-server": { kind: "web", folder: "hw-web-server" },
  "dojo-git": dojoGuide("linux", "dojo-git", "Git 道場"),
  "dojo-cloud": dojoGuide("python", "dojo-cloud", "クラウド道場"),
  "dojo-design": dojoGuide("python", "dojo-design", "設計道場"),
  "dojo-cs": dojoGuide("python", "dojo-cs", "コンピュータ基礎道場"),
  "dojo-os": dojoGuide("python", "dojo-os", "OS 道場"),
  "dojo-auth": dojoGuide("python", "dojo-auth", "認証・認可道場"),
  "dojo-docker": dojoGuide("python", "dojo-docker", "Docker・CI/CD 道場"),
  "oop-armory": { kind: "python", folder: "oop-armory" },
  "numpy-training": { kind: "python", folder: "numpy-training" },
  "pandas-teahouse": { kind: "python", folder: "pandas-teahouse" },
  "viz-teahouse": { kind: "python", folder: "viz-teahouse" },
  "ml-teahouse": { kind: "python", folder: "ml-teahouse" },
  "nn-gatekeeper": { kind: "python", folder: "nn-gatekeeper" },
  // 実践プロジェクト② タスク管理アプリ。4 章とも同じフォルダ (~/taskapp) を使い続ける
  "taskapp-ch8": { kind: "node", folder: "taskapp" },
  "taskapp-ch9": { kind: "node", folder: "taskapp" },
  "taskapp-ch10": { kind: "node", folder: "taskapp" },
  "taskapp-ch11": { kind: "node", folder: "taskapp" },
  // 実践プロジェクト① ECサイト。Tomcat と Maven の部品はイメージにある
  "ecsite-ch7-2": { kind: "java", folder: "ec-site" },
  // Linux 道場の 10 問 (dojo-lx-guides.ts)。1 問が 1 章で、フォルダも 1 問ずつ
  "dojo-lx-01": { kind: "linux", folder: "dojo-lx-01" },
  "dojo-lx-02": { kind: "linux", folder: "dojo-lx-02" },
  "dojo-lx-03": { kind: "linux", folder: "dojo-lx-03" },
  "dojo-lx-04": { kind: "linux", folder: "dojo-lx-04" },
  "dojo-lx-05": { kind: "linux", folder: "dojo-lx-05" },
  "dojo-lx-06": { kind: "linux", folder: "dojo-lx-06" },
  "dojo-lx-07": { kind: "linux", folder: "dojo-lx-07" },
  "dojo-lx-08": { kind: "linux", folder: "dojo-lx-08" },
  "dojo-lx-09": { kind: "linux", folder: "dojo-lx-09" },
  "dojo-lx-10": { kind: "linux", folder: "dojo-lx-10" },
  // プロンプト道場の 10 問 (dojo-pr-guides.ts)。1 問が 1 章で、フォルダも 1 問ずつ。判定が python3 なので kind は python
  "dojo-pr-01": { kind: "python", folder: "dojo-pr-01" },
  "dojo-pr-02": { kind: "python", folder: "dojo-pr-02" },
  "dojo-pr-03": { kind: "python", folder: "dojo-pr-03" },
  "dojo-pr-04": { kind: "python", folder: "dojo-pr-04" },
  "dojo-pr-05": { kind: "python", folder: "dojo-pr-05" },
  "dojo-pr-06": { kind: "python", folder: "dojo-pr-06" },
  "dojo-pr-07": { kind: "python", folder: "dojo-pr-07" },
  "dojo-pr-08": { kind: "python", folder: "dojo-pr-08" },
  "dojo-pr-09": { kind: "python", folder: "dojo-pr-09" },
  "dojo-pr-10": { kind: "python", folder: "dojo-pr-10" },
  // 宿題の図の章 (homework-diagram-guides.ts)。確かめが python3 で図の字を読む
  "hw-web-flow": { kind: "python", folder: "hw-web-flow" },
  "hw-login-flow": { kind: "python", folder: "hw-login-flow" },
  // React 道場の 10 問 (dojo-re-guides.ts)。1 問が 1 章で、フォルダも 1 問ずつ
  "dojo-re-01": { kind: "web", folder: "dojo-re-01" },
  "dojo-re-02": { kind: "web", folder: "dojo-re-02" },
  "dojo-re-03": { kind: "web", folder: "dojo-re-03" },
  "dojo-re-04": { kind: "web", folder: "dojo-re-04" },
  "dojo-re-05": { kind: "web", folder: "dojo-re-05" },
  "dojo-re-06": { kind: "web", folder: "dojo-re-06" },
  "dojo-re-07": { kind: "web", folder: "dojo-re-07" },
  "dojo-re-08": { kind: "web", folder: "dojo-re-08" },
  "dojo-re-09": { kind: "web", folder: "dojo-re-09" },
  "dojo-re-10": { kind: "web", folder: "dojo-re-10" },
  "ai-dev-01": { kind: "node", folder: "ai-dev-01" },
  "ai-dev-02": { kind: "node", folder: "ai-dev-02" },
  "ai-dev-03": { kind: "node", folder: "ai-dev-03" },
  "ai-dev-04": { kind: "node", folder: "ai-dev-04" },
  "ai-dev-05": { kind: "node", folder: "ai-dev-05" },
  "mole-game": { kind: "web", folder: "mole-game" },
  maze: { kind: "python", folder: "maze" },
  "kakeibo-graph": { kind: "python", folder: "kakeibo-graph" },
  "oshi-ranking": { kind: "db", folder: "oshi-ranking" },
  "typing-game": { kind: "web", folder: "typing-game" },
  "chat-room": { kind: "node", folder: "chat-room" },
};

/** 手で書いた章か。中身は guides.ts にあり、読みに行かない */
export function isWrittenGuide(id: string): boolean {
  return Object.hasOwn(WRITTEN_GUIDE_INDEX, id);
}

/** 章を ID で引く。constructor などの継承の名前は章として扱わない */
export function findGuideEntry(id: string | null | undefined): GuideEntry | null {
  if (!id) return null;
  const entry = Object.hasOwn(WRITTEN_GUIDE_INDEX, id)
    ? WRITTEN_GUIDE_INDEX[id]
    : Object.hasOwn(LESSON_GUIDE_INDEX, id) ? LESSON_GUIDE_INDEX[id] : null;
  return entry ? { id, ...entry } : null;
}

/** 索引にある章の ID。手で書いた章が先 */
export function guideIds(): string[] {
  return [...Object.keys(WRITTEN_GUIDE_INDEX), ...Object.keys(LESSON_GUIDE_INDEX)];
}
